/**
 * Sending the browser's errors to the owner of the system.
 *
 * Every uncaught error and every screen that crashes is reported once - the
 * same message is not sent again for a minute, and no more than twenty per
 * visit - so a fault in a loop cannot flood anything. Noise that is not ours
 * (browser extensions, the ResizeObserver warning, a dropped connection) is
 * left out. A report that cannot be sent is simply dropped: reporting an
 * error must never become an error of its own, or slow the till down.
 */
import { shopKey, thisShop } from './shop.js';

type App = 'till' | 'panel';
let app: App = 'till';
const sentAt = new Map<string, number>();
let sent = 0;

const NOISE = [
  /ResizeObserver loop/i,
  /Failed to fetch/i,
  /NetworkError/i,
  /Load failed/i,
  /AbortError/i,
  /chrome-extension:|moz-extension:|safari-extension:/i,
];

function who(): string | null {
  try {
    const raw = localStorage.getItem(shopKey('pos.user'));
    return raw ? String((JSON.parse(raw) as { username?: string }).username ?? '') || null : null;
  } catch {
    return null;
  }
}

export function reportError(err: unknown, where?: string): void {
  try {
    const e = err instanceof Error ? err : new Error(typeof err === 'string' ? err : JSON.stringify(err));
    const message = `${e.name && e.name !== 'Error' ? `${e.name}: ` : ''}${e.message}`.slice(0, 500);
    const stack = `${e.stack ?? ''}${where ? `\n--- component ---\n${where}` : ''}`.slice(0, 4000);
    if (!message || NOISE.some((r) => r.test(message) || r.test(stack))) return;
    // A crash caught by a screen carries which component it was in; in
    // development React also throws it to the page first without that. Let the
    // fuller report through even right after the bare one.
    const key = `${message}${where ? '|component' : ''}`;
    const last = sentAt.get(key) ?? 0;
    if (Date.now() - last < 60_000 || sent >= 20) return;
    sentAt.set(key, Date.now());
    sent++;
    void fetch('/api/errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        app,
        account: thisShop()?.code ?? null,
        user: app === 'till' ? who() : null,
        url: window.location.pathname,
        message,
        stack,
      }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* never throw from here */
  }
}

export function installErrorReporting(which: App): void {
  app = which;
  window.addEventListener('error', (ev) => reportError(ev.error ?? ev.message));
  window.addEventListener('unhandledrejection', (ev) => reportError(ev.reason));
}
